/** Contract Renewals (Demo Mode only) - customer-first view of upcoming contract renewals for
 * the Customer Management workspace. Surfaces notice periods and renewal timers so users can
 * answer "which contracts or renewals need attention", then jump into the customer workspace's
 * Contracts tab to act. All figures are static mock data, matching the "seeded illustrative
 * numbers" approach used by `src/pages/EstateManagement`. */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Chip, Grid, IconButton, SvgIcon, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, useTheme,
} from '@mui/material';
import {
  Assignment, Timelapse, EventNote, ArrowForward,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { Section } from '../../../components/ui/Section';
import { TableHeader } from '../../../components/forms/TableHeader';
import { Pill } from '../../../components/shared/Pill';
import CustomPagination from '../../../components/forms/Pagination';
import { getAllCustomers, isOnboardingCustomer } from '../../../mocks/data/customerManagementData';

const ContractRenewals = () => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);

  const customers = getAllCustomers();

  const rows = useMemo(
    () => [...customers].sort((a, b) => new Date(a.renewalDate).getTime() - new Date(b.renewalDate).getTime()),
    [customers],
  );

  const renewalsDue = customers.filter((customer) => customer.status === 'Renewal Due').length;

  const METRIC_TILES = [
    { label: t('contractRenewalsActive', 'Active contracts'), value: `${customers.length}`, subtitle: t('contractRenewalsActiveSub', 'Across the portfolio'), icon: <Assignment />, color: '#1976d2' },
    { label: t('contractRenewalsDue', 'Renewals due'), value: `${renewalsDue}`, subtitle: t('contractRenewalsDueSub', 'Needs renewal action'), icon: <Timelapse />, color: '#ed6c02' },
    { label: t('contractRenewalsAvgNotice', 'Avg. notice period'), value: '4.9 months', subtitle: t('contractRenewalsAvgNoticeSub', 'Across the portfolio'), icon: <EventNote />, color: '#9c27b0' },
  ];

  return (
    <PageArea>
      <PageTitle subTitle={t('contractRenewalsSubTitle', 'Track contract end dates, notice periods and renewal timers so nothing lapses without action.')}>
        {t('contractRenewalsTitle', 'Contract Renewals')}
      </PageTitle>

      <Grid container
        spacing={4}
        sx={{ mb: 6 }}>
        {METRIC_TILES.map((tile) => (
          <Grid item
            xs={12}
            sm={6}
            md={4}
            key={tile.label}>
            <Section style={{ p: 4, mb: 0 }}>
              <Box display="flex"
                alignItems="center"
                justifyContent="space-between">
                <Box flex={1}>
                  <Box sx={{ color: theme.palette.custom.gray[600], fontSize: '10px', textTransform: 'uppercase', mb: 0.25, fontWeight: 500 }}>
                    {tile.label}
                  </Box>
                  <Box sx={{ fontSize: '20px', fontWeight: 600, color: theme.palette.custom.gray[900], mb: 0.25 }}>
                    {tile.value}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: theme.palette.custom.gray[600] }}>
                    {tile.subtitle}
                  </Box>
                </Box>
                <Box sx={{ backgroundColor: `${tile.color}20`, borderRadius: '50%', width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', color: tile.color }}>
                  <SvgIcon fontSize="medium">{tile.icon}</SvgIcon>
                </Box>
              </Box>
            </Section>
          </Grid>
        ))}
      </Grid>

      <Section>
        <TableHeader>
          {t('contractRenewalsListTitle', 'Upcoming renewals')}
          <Pill text={`${rows.length} ${t('contractRenewalsCountLabel', 'contracts')}`} />
        </TableHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('contractRenewalsCustomer', 'Client')}</TableCell>
                <TableCell>{t('contractRenewalsRef', 'Contract Ref.')}</TableCell>
                <TableCell>{t('contractRenewalsEndDate', 'End Date')}</TableCell>
                <TableCell>{t('contractRenewalsNoticePeriod', 'Notice Period')}</TableCell>
                <TableCell>{t('contractRenewalsRenewalTimer', 'Renewal Timer')}</TableCell>
                <TableCell>{t('contractRenewalsStatus', 'Status')}</TableCell>
                <TableCell>{t('contractRenewalsActions', 'Actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((customer) => (
                <TableRow key={customer.id}
                  hover>
                  <TableCell>{customer.name}</TableCell>
                  <TableCell>{customer.contract.contractRef}</TableCell>
                  <TableCell>{customer.contract.endDate}</TableCell>
                  <TableCell>{customer.contract.noticePeriod}</TableCell>
                  <TableCell>{customer.contract.renewalTimer}</TableCell>
                  <TableCell>
                    <Chip label={customer.contract.status}
                      size="small"
                      color={customer.contract.status === 'Renewal Due' ? 'warning' : 'success'}
                      variant="outlined" />
                  </TableCell>
                  <TableCell>
                    <IconButton size="small"
                      title={t('contractRenewalsOpenWorkspace', 'Open client workspace')}
                      onClick={() => navigate(isOnboardingCustomer(customer) ? `/customer-management/onboarding/${customer.id}` : `/customer-management/customers/${customer.id}`)}>
                      <ArrowForward fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <CustomPagination page={page}
          setPage={setPage}
          maxPages={1}
          useNoDataMessage={false}
          breakpointForChangeDisplay={120000} />
      </Section>
    </PageArea>
  );
};

export default ContractRenewals;
